import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { ProductBatchDto, ProductType, UpsertWorkbenchGeneratorInstanceDto, WorkbenchDto } from '@services/dtos'
import { upsertWorkbenchGeneratorSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Alert, Autocomplete, Button, Divider, Grid, TextField, Typography } from '@mui/material'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import SelectField from '../inputs/SelectField'
import { FormHeader } from './FormHeader'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { productBatchService } from '@services/ProductBatchService'
import InputField from '../inputs/InputField'
import { upsertGeneratorInstance, removeGeneratorInstance } from '@state/reducers/workbenchReducer'
import { mbqToGbq } from '../Helpers'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import moment from 'moment'

type Props = {
  workbench: WorkbenchDto
  onClose: () => void
}

const WorkbenchGeneratorInstanceForm: React.FC<Props> = ({ workbench, onClose }) => {
  const [selectedProductBatch, setSelectedProductBatch] = useState<ProductBatchDto | null>(null)
  const { handleSubmit, setValue, control, watch, formState: { errors } } = useForm<UpsertWorkbenchGeneratorInstanceDto>({
    resolver: yupResolver(upsertWorkbenchGeneratorSchema),
    defaultValues: {
      generatorSlotId: workbench.generatorSlots.length === 1 ? workbench.generatorSlots[0].id : undefined,
    },
  })
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  const onSubmitForm = async ({ generatorSlotId, activityAtRefGbq, ...dto }: UpsertWorkbenchGeneratorInstanceDto) => {
    if (!generatorSlotId || !activityAtRefGbq) {
      return
    }
    
    setIsSubmitting(true)

    dispatch(upsertGeneratorInstance({ id: workbench.id, generatorSlotId, dto: { ...dto, activityAtRefMbq: activityAtRefGbq * 1000 } }))
      .unwrap()
      .then(() => {
        cb('Générateur modifié avec succès')
        onClose()
      })
      .catch(() => cb('Une erreur est survenue'))
      .finally(() => setIsSubmitting(false))
  }

  const fetchProductBatches = async (search: string) => {
    const page = await productBatchService.getAll({ search, inStock: true, productType: ProductType.GENERATOR })
    return page?.datas ?? []
  }

  const currentGeneratorInstance = workbench.generatorSlots.find(slot => slot.id === watch('generatorSlotId'))?.generatorInstance

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }} 
        className="w-full">
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose} 
          title={'Modifier générateurs'}
          subTitles={[`Enceinte blindée ${workbench.name} - ${workbench.generatorSlots.length} emplacement${workbench.generatorSlots.length > 1 ? 's' : ''}`]}
        />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectField
              control={control}
              name={'generatorSlotId'}
              options={workbench.generatorSlots.map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              value={watch('generatorSlotId')}
              label={'Emplacement'}
            />

            {watch('generatorSlotId') && currentGeneratorInstance && (
              <Alert severity="info">
                Générateur sur cet emplacement : {`${currentGeneratorInstance?.productBatch.productCatalog.name}${' '}
                  ${currentGeneratorInstance?.serialNumber}` || 'Aucun'}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <ServerAutocomplete<ProductBatchDto>
              fetchService={fetchProductBatches}
              onChange={(value: ProductBatchDto | null) => {
                setValue('productBatchId', value?.id ?? '')
                setSelectedProductBatch(value)
                setValue('activityRefAt', value?.activityRefAt)
                setValue('activityAtRefGbq', value?.activityAtRefMbq ? mbqToGbq(value.activityAtRefMbq) : undefined)
              }}
              getValue={(value) => value?.id?.toString() || null}
              getOptionLabel={(value) => {
                const productBatch = value as ProductBatchDto
                return productBatch ? `${productBatch.lotNumber} (${productBatch.productCatalog.name}${productBatch.activityAtRefMbq ? 
                  ` - ${mbqToGbq(productBatch.activityAtRefMbq)} Gbq` : ''})` : ''
              }}
              debounceMs={300}
              placeholder="Rechercher un lot de produits (n° de lot ou nom du produit)"
              label={`Lot de produits${currentGeneratorInstance ? ' de remplacement' : ''}`}
              helperText={errors.productBatchId?.message}
              error={!!errors.productBatchId}
              minSearchLength={0}
            />
          </Grid>
          {selectedProductBatch && (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  options={selectedProductBatch?.storageLocations.filter(({ units }) => units > 0) ?? []}
                  getOptionLabel={(option) => option.storageLocation.name}
                  value={
                    selectedProductBatch?.storageLocations.find(
                      sl => sl.storageLocation.id === watch('storageLocationId')
                    ) ?? null
                  }
                  onChange={(_event, value) => { setValue('storageLocationId', value?.storageLocation.id)}}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={'Stockage'}
                      placeholder="Sélectionner un stockage"
                      error={!!errors.storageLocationId}
                      helperText={errors.storageLocationId?.message}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.storageLocation.id === value.storageLocation.id}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  control={control}
                  name={'activityAtRefGbq'}
                  label={'Dose GBq'}
                  type="number"
                  error={!!errors.activityAtRefGbq}
                  helperText={errors.activityAtRefGbq?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Date calibration (UTC)"
                  name={'activityRefAt'}
                  value={(() => {
                    const stored = watch('activityRefAt')
                    if (!stored) {
                      return null
                    }
                    const d = new Date(stored)
                    return moment(new Date(
                      d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
                      d.getUTCHours(), d.getUTCMinutes()
                    ))
                  })()}
                  onChange={(date) => {
                    if (!date) {
                      return setValue('activityRefAt', undefined)
                    }
                    const utcDate = new Date(Date.UTC(
                      date.year(), date.month(), date.date(),
                      date.hour(), date.minute(), 0
                    ))
                    setValue('activityRefAt', utcDate)
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.activityRefAt,
                      helperText: errors.activityRefAt?.message,
                    },
                  }}
                  disableFuture
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <InputField
              control={control}
              name={'serialNumber'}
              label={`Numéro de série du générateur${currentGeneratorInstance ? ' de remplacement' : ''}`}
            />
          </Grid>

          {currentGeneratorInstance && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <Typography variant="body2">ou</Typography>
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    const generatorSlotId = watch('generatorSlotId')
                    if (!generatorSlotId) {
                      return
                    }

                    setIsSubmitting(true)

                    dispatch(removeGeneratorInstance({
                      id: workbench.id, 
                      generatorSlotId
                    }))
                      .unwrap()
                      .then(() => {
                        cb('Générateur supprimé avec succès')
                        onClose()
                      })
                      .catch(() => cb('Une erreur est survenue'))
                      .finally(() => setIsSubmitting(false))
                  }}
                >
                  Supprimer le générateur de cet emplacement
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </form>
    </div>
  )
}

export default WorkbenchGeneratorInstanceForm
